
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppSidebar } from '@/components/AppSidebar';
import { useAnimatedSidebar } from '@/components/ui/animated-sidebar';
import { useAuth } from '@/context/AuthContext';

interface LayoutProps {
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { width } = useAnimatedSidebar();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentChatId, setCurrentChatId] = useState<string | null>(
    localStorage.getItem('currentChatId')
  );
  
  // Listen for chat ID changes
  useEffect(() => {
    const handleChatChange = (e: StorageEvent) => {
      if (e.key === 'currentChatId') {
        setCurrentChatId(e.newValue);
      }
    };
    
    window.addEventListener('storage', handleChatChange);
    
    const handleNewConversation = (e: Event) => {
      const customEvent = e as CustomEvent;
      console.log("Layout: New conversation event received:", customEvent.detail);
      
      // Set current chat ID if provided in event
      if (customEvent.detail?.chatId) {
        setCurrentChatId(customEvent.detail.chatId);
        
        // Navigate to builder with this chat ID if not already there
        if (!location.pathname.includes('/builder')) {
          navigate('/builder');
        }
      } else {
        // Clear current chat ID when starting a new conversation without ID
        setCurrentChatId(null);
      }
    };
    
    const handleOpenChat = (e: CustomEvent) => {
      if (e.detail?.chatId) {
        console.log("Layout: Open chat event received:", e.detail.chatId);
        setCurrentChatId(e.detail.chatId);
        
        // Navigate to builder with this chat ID if not already there
        if (!location.pathname.includes('/builder')) {
          navigate('/builder');
        }
      }
    };
    
    window.addEventListener('start-new-conversation', handleNewConversation as EventListener);
    window.addEventListener('open-chat', handleOpenChat as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleChatChange);
      window.removeEventListener('start-new-conversation', handleNewConversation as EventListener);
      window.removeEventListener('open-chat', handleOpenChat as EventListener);
    };
  }, [location, navigate]);
  
  // Update document title based on current page
  useEffect(() => {
    const pathToTitle = {
      '/': 'Home',
      '/builder': 'E-Mail Builder',
      '/chats': 'Chat Verlauf',
      '/templates': 'Templates',
      '/offers': 'Angebote',
      '/account': 'Konto',
      '/settings': 'Einstellungen',
      '/vibesign': 'VibeSign',
      '/documentation': 'Dokumentation'
    };
    
    const path = Object.keys(pathToTitle).find(p => 
      location.pathname === p || location.pathname.startsWith(`${p}/`)
    );
    
    document.title = path 
      ? `${pathToTitle[path as keyof typeof pathToTitle]} | Vibemail` 
      : 'Vibemail';
  }, [location]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-gradient-to-br from-black to-gray-900">
      <AppSidebar currentChatId={currentChatId} />
      <div 
        className="flex-1 h-full overflow-hidden flex flex-col"
        style={{ 
          marginLeft: `${width.collapsed}px`,
          transition: 'margin-left 0.3s ease' 
        }}
      >
        {children || <Outlet />}
      </div>
    </div>
  );
};

export default Layout;
